<script lang="ts">
  import { type Warehouse, type WarehouseFormData } from '../../lib/types';

  let {
    isOpen,
    mode,
    warehouse = null,
    isLoading,
    error,
    onsubmit,
    onclose,
  }: {
    isOpen: boolean;
    mode: 'add' | 'edit';
    warehouse?: Warehouse | null;
    isLoading: boolean;
    error: string;
    onsubmit: (data: WarehouseFormData) => void;
    onclose: () => void;
  } = $props();

  let name = $state('');
  let contactPerson = $state('');
  let email = $state('');
  let phone = $state('');
  let mobile = $state('');
  let addressLine1 = $state('');
  let addressLine2 = $state('');
  let suburb = $state('');
  let postcode = $state('');
  let stateField = $state('');
  let country = $state('Australia');
  let address = $state('');

  // Reset form when modal opens
  $effect(() => {
    if (isOpen) {
      if (mode === 'edit' && warehouse) {
        name = warehouse.name;
        contactPerson = warehouse.contactPerson ?? '';
        email = warehouse.email ?? '';
        phone = warehouse.phone ?? '';
        mobile = warehouse.mobile ?? '';
        addressLine1 = warehouse.addressLine1 ?? '';
        addressLine2 = warehouse.addressLine2 ?? '';
        suburb = warehouse.suburb ?? '';
        postcode = warehouse.postcode ?? '';
        stateField = warehouse.state ?? '';
        country = warehouse.country ?? 'Australia';
        address = warehouse.address ?? '';
      } else {
        name = '';
        contactPerson = '';
        email = '';
        phone = '';
        mobile = '';
        addressLine1 = '';
        addressLine2 = '';
        suburb = '';
        postcode = '';
        stateField = '';
        country = 'Australia';
        address = '';
      }
    }
  });

  function handleSubmit(e: Event) {
    e.preventDefault();
    onsubmit({
      name,
      contactPerson,
      email,
      phone,
      mobile,
      addressLine1,
      addressLine2,
      suburb,
      postcode,
      state: stateField,
      country,
      address,
    });
  }

  const isAdd = $derived(mode === 'add');
  const title = $derived(isAdd ? 'Add Warehouse' : 'Edit Warehouse');
  const submitLabel = $derived(isAdd
    ? (isLoading ? 'Adding...' : 'Add Warehouse')
    : (isLoading ? 'Saving...' : 'Save Changes')
  );
  const idPrefix = $derived(isAdd ? 'add' : 'edit');
</script>

{#if isOpen}
  <div class="modal-overlay" onclick={onclose}>
    <div class="modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h3>{title}</h3>
        <button class="btn-close" onclick={onclose}>&times;</button>
      </div>

      <form onsubmit={handleSubmit}>
        {#if error}
          <div class="form-error">{error}</div>
        {/if}

        <div class="form-group">
          <label for="{idPrefix}-warehouse-name">Warehouse Name *</label>
          <input
            id="{idPrefix}-warehouse-name"
            type="text"
            bind:value={name}
            placeholder="e.g., Main Warehouse, Brisbane DC"
            disabled={isLoading}
            autofocus={isAdd}
          />
        </div>

        <div class="form-group">
          <label for="{idPrefix}-warehouse-contact">Contact Person</label>
          <input
            id="{idPrefix}-warehouse-contact"
            type="text"
            bind:value={contactPerson}
            placeholder="Enter contact person name (optional)"
            disabled={isLoading}
          />
        </div>

        <div class="form-section-divider"></div>
        <h4 class="form-section-title">Contact Information</h4>

        <div class="form-group">
          <label for="{idPrefix}-warehouse-email">Email</label>
          <input
            id="{idPrefix}-warehouse-email"
            type="email"
            bind:value={email}
            placeholder="email@example.com"
            disabled={isLoading}
          />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="{idPrefix}-warehouse-phone">Phone</label>
            <input
              id="{idPrefix}-warehouse-phone"
              type="tel"
              bind:value={phone}
              placeholder="(02) 1234 5678"
              disabled={isLoading}
            />
          </div>
          <div class="form-group">
            <label for="{idPrefix}-warehouse-mobile">Mobile</label>
            <input
              id="{idPrefix}-warehouse-mobile"
              type="tel"
              bind:value={mobile}
              placeholder="0400 123 456"
              disabled={isLoading}
            />
          </div>
        </div>

        <div class="form-section-divider"></div>
        <h4 class="form-section-title">Address</h4>

        <div class="form-group">
          <label for="{idPrefix}-warehouse-address1">Street Address</label>
          <input
            id="{idPrefix}-warehouse-address1"
            type="text"
            bind:value={addressLine1}
            placeholder="Street address"
            disabled={isLoading}
          />
        </div>

        <div class="form-group">
          <label for="{idPrefix}-warehouse-address2">Address Line 2</label>
          <input
            id="{idPrefix}-warehouse-address2"
            type="text"
            bind:value={addressLine2}
            placeholder="Apt, suite, unit, etc. (optional)"
            disabled={isLoading}
          />
        </div>

        <div class="form-group">
          <label for="{idPrefix}-warehouse-suburb">Suburb</label>
          <input
            id="{idPrefix}-warehouse-suburb"
            type="text"
            bind:value={suburb}
            placeholder="Suburb"
            disabled={isLoading}
          />
        </div>

        <div class="form-row form-row-3">
          <div class="form-group">
            <label for="{idPrefix}-warehouse-postcode">Postcode</label>
            <input
              id="{idPrefix}-warehouse-postcode"
              type="text"
              bind:value={postcode}
              placeholder="4000"
              disabled={isLoading}
            />
          </div>
          <div class="form-group">
            <label for="{idPrefix}-warehouse-state">State</label>
            <input
              id="{idPrefix}-warehouse-state"
              type="text"
              bind:value={stateField}
              placeholder="QLD"
              disabled={isLoading}
            />
          </div>
          <div class="form-group">
            <label for="{idPrefix}-warehouse-country">Country</label>
            <input
              id="{idPrefix}-warehouse-country"
              type="text"
              bind:value={country}
              placeholder="Australia"
              disabled={isLoading}
            />
          </div>
        </div>

        {#if isAdd}
          <p class="form-help">
            This warehouse will be available for inventory management in the EQUOS app.
          </p>
        {/if}

        <div class="modal-actions">
          <button type="button" class="btn-secondary" onclick={onclose} disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" class="btn-primary" disabled={isLoading}>
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
